import { useState } from "react";
import { format, parse, isValid } from "date-fns";

const formatSalesDate = (dateStr: string) => {
  if (!dateStr) return "-";

  // if it's already YYYY-MM-DD or parseable by JS Date natively
  const standardDate = new Date(dateStr);
  if (isValid(standardDate)) {
    return format(standardDate, "dd-MM-yyyy");
  }

  // if it's DD-MM-YYYY format
  const parsedDate = parse(dateStr, "dd-MM-yyyy", new Date());
  if (isValid(parsedDate)) {
    return format(parsedDate, "dd-MM-yyyy");
  }

  return dateStr;
};
import {
  BadgeDollarSignIcon,
  ChevronLeft,
  ChevronRight,
  Newspaper,
  Printer,
  Search,
} from "lucide-react";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import { useDebounce } from "use-debounce";
import { useNavigate } from "react-router-dom";

import SlipModal from "../components/forms/SlipModal";
import DeliveryNoteModal, {
  DeliveryNoteFormValues,
} from "../components/forms/DeliveryNoteModal";
import InvoiceDownloadDialog from "../components/InvoiceDownloadDialog";
import { Button } from "../utils/components/ui/button";
import {
  useCreateDeliveryNote,
  useDownloadDeliveryNotePdf,
  useGetAllInvoice,
  useGetASalesInvoice,
  usePrintInvoice,
  useRevokeInvoice,
} from "../service/invoiseService";
import Loading from "../components/common/LoaderCommon";
import { cn } from "../utils/cn";
import { useQueryManager } from "../hooks/useQueryManager";

const Invoice = () => {
  const navigate = useNavigate();
  const [slipModalOpen, setSlipModalOpen] = useState(false);
  const [slipTransactions, setSlipTransactions] = useState<
    { id: string; name: string; date: string }[]
  >([]);
  const [slipLoading] = useState(false);
  const [deliveryNoteModalOpen, setDeliveryNoteModalOpen] = useState(false);
  const [selectedDeliveryInvoice, setSelectedDeliveryInvoice] = useState<
    any | null
  >(null);
  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);
  const [downloadInvoiceId, setDownloadInvoiceId] = useState<string | null>(
    null,
  );

  const { setValue, allParams } = useQueryManager();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const [debouncedSearchKey] = useDebounce(allParams.search, 500);
  const [paymentStatus, setPaymentStatus] = useState("");

  const { data: invoiceData, isLoading } = useGetAllInvoice({
    pageNo: page,
    pageSize: 50,
    invoiceNumber: debouncedSearchKey || "",
    paymentStatus: paymentStatus || "",
  });

  const createDeliveryNote = useCreateDeliveryNote();

  const downloadDeliveryNotePdf = useDownloadDeliveryNotePdf({
    onSuccess: (blob: Blob, variables: { deliveryNoteId: string }) => {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `delivery-note-${variables.deliveryNoteId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Delivery note PDF downloaded successfully");
      queryClient.invalidateQueries({ queryKey: ["useGetAllInvoice"] });
    },
    onError: (error: Error) => {
      toast.error(`Failed to download delivery note PDF: ${error.message}`);
    },
  });

  const PrintInvoice = usePrintInvoice({
    onSuccess: (data: string) => {
      const printWindow = window.open("", "_blank");
      if (printWindow) {
        printWindow.document.open();
        printWindow.document.write(data);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
      } else {
        toast.error("Popup blocked! Please allow popups for this site.");
      }
      queryClient.invalidateQueries({ queryKey: ["useGetAllInvoice"] });
    },
    onError: (error: Error) => {
      toast.error(`Error printing invoice: ${error.message}`);
    },
  });

  useRevokeInvoice();

  const invoiceRows = invoiceData?.invoiceResponse ?? [];
  const totalInvoices = invoiceRows.length;

  const selectedDeliveryInvoiceId = selectedDeliveryInvoice?.id
    ? String(selectedDeliveryInvoice.id)
    : "";
  const { data: selectedDeliveryInvoiceDetail } = useGetASalesInvoice(
    selectedDeliveryInvoiceId || undefined,
  );
  const deliveryInvoice =
    selectedDeliveryInvoiceDetail ?? selectedDeliveryInvoice;

  const handleCreateDeliveryNote = (values: DeliveryNoteFormValues) => {
    if (!selectedDeliveryInvoice) {
      toast.error("Select an invoice before creating a delivery note");
      return;
    }

    const invoiceId = String(selectedDeliveryInvoice.id);
    const items = values.items.filter((item) => item.quantity > 0);

    if (items.length === 0) {
      toast.error("Enter at least one delivery quantity");
      return;
    }

    createDeliveryNote.mutate(
      { invoiceId: Number(invoiceId), items },
      {
        onSuccess: (response) => {
          toast.success("Delivery note created successfully");
          // Refresh the notes list inside the modal
          queryClient.invalidateQueries({
            queryKey: ["useGetDeliveryNotesByInvoice", invoiceId],
          });
          if (response?.id) {
            downloadDeliveryNotePdf.mutate({
              deliveryNoteId: String(response.id),
            });
          } else {
            queryClient.invalidateQueries({ queryKey: ["useGetAllInvoice"] });
          }
        },
        onError: (error: Error) => {
          toast.error(`Failed to create delivery note: ${error.message}`);
        },
      },
    );
  };

  if (isLoading) {
    return <Loading />;
  }

  return (
    <>
      <div className="space-y-6">
        <div className="flex justify-end">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
            <Button
              className="h-11 rounded-[12px] bg-[#1A5EF9] px-6 text-white hover:bg-[#0E4FDE]"
              onClick={() => navigate("/create-invoice")}
            >
              Create New Invoice
            </Button>
          </div>
        </div>

        <div className="rounded-[26px] border border-[#E8EEF9] bg-white p-6 shadow-[0_16px_40px_rgba(26,94,249,0.08)]">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="relative min-w-[300px] max-w-[460px] flex-1">
              <Search className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#91A0B8]" />
              <input
                autoFocus={!!(allParams.search && allParams.search.length > 0)}
                type="text"
                placeholder="Search by invoice number"
                className="h-12 w-full rounded-[14px] border border-[#E5ECF8] bg-[#F5F8FD] pl-11 pr-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]"
                onChange={(e) => setValue("search", e.target.value)}
                value={allParams.search}
              />
            </div>

            <div className="flex items-center gap-3">
              <select
                value={paymentStatus}
                onChange={(e) => {
                  setPaymentStatus(e.target.value);
                  setPage(0);
                }}
                className="h-12 min-w-[160px] rounded-[14px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 text-sm font-medium text-[#5D6E8A] outline-none transition hover:border-[#BFD1FF] focus:border-[#BFD1FF]"
              >
                <option value="">All Status</option>
                <option value="OPENING_BALANCE">Opening Balance</option>
                <option value="PAID">Paid</option>
                <option value="PARTIALLY_PAID">Partially Paid</option>
                <option value="CREDIT">Credit</option>
                <option value="OVERDUE">Overdue</option>
              </select>
              {paymentStatus && (
                <button
                  onClick={() => {
                    setPaymentStatus("");
                    setPage(0);
                  }}
                  className="text-sm font-medium text-[#D14343] transition hover:text-[#AF2B2B]"
                  title="Clear status filter"
                  type="button"
                >
                  Clear
                </button>
              )}
              <span className="rounded-[12px] bg-[#F5F8FD] px-4 py-2 text-sm text-[#7D8998]">
                <span className="font-semibold text-[#253650]">
                  {totalInvoices}
                </span>{" "}
                invoices found
              </span>
              <div className="flex items-center gap-2">
                <button
                  className={cn(
                    "rounded-[10px] border border-[#D7E2F5] p-2 text-[#52627C] transition hover:bg-[#F5F8FD]",
                    { "cursor-not-allowed opacity-50": page === 0 },
                  )}
                  disabled={page === 0}
                  onClick={() => setPage(page - 1)}
                >
                  <ChevronLeft className="size-4" />
                </button>
                <div className="rounded-[10px] bg-[#1A5EF9] px-3 py-2 text-sm font-semibold text-white">
                  {page + 1}
                </div>
                <button
                  className={cn(
                    "rounded-[10px] border border-[#D7E2F5] p-2 text-[#52627C] transition hover:bg-[#F5F8FD]",
                    {
                      "cursor-not-allowed opacity-50":
                        invoiceData?.totalPages === page,
                    },
                  )}
                  disabled={invoiceData?.totalPages === page}
                  onClick={() => setPage(page + 1)}
                >
                  <ChevronRight className="size-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-[26px] border border-[#E8EEF9] bg-white shadow-[0_16px_40px_rgba(26,94,249,0.08)]">
          <div className="flex flex-col gap-4 border-b border-[#E8EEF9] px-6 py-5 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h3 className="text-lg font-semibold text-[#21314A]">
                Invoice Directory
              </h3>
              <p className="mt-1 text-sm text-[#7D8998]">
                Print invoices, review payment slips, and manage delivery note
                output from one listing.
              </p>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="bg-[#F5F8FD] text-left text-sm font-semibold text-[#31425E]">
                  <th className="px-6 py-4">Invoice Number</th>
                  <th className="px-6 py-4">Customer</th>
                  <th className="px-6 py-4">Invoice Date</th>
                  <th className="px-6 py-4 text-right">Total Amount</th>
                  <th className="px-6 py-4 text-right">Amount Received</th>
                  <th className="px-6 py-4">Status</th>
                  <th className="px-6 py-4 text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {invoiceRows?.length ? (
                  invoiceRows?.map((item: any) => (
                    <tr
                      key={item.id}
                      className="border-t border-[#EEF2FA] text-sm text-[#43536A] transition-colors hover:bg-[#FAFCFF]"
                    >
                      <td className="px-6 py-4 font-semibold text-[#24344F]">
                        {item.invoiceNumber}
                      </td>
                      <td className="px-6 py-4">
                        {item.customer?.username || "-"}
                      </td>
                      <td className="px-6 py-4">
                        {formatSalesDate(item?.salesDate)}
                      </td>
                      <td className="px-6 py-4 text-right font-medium text-[#24344F]">
                        {Number(item.totalAmount || 0).toFixed(3)}
                      </td>
                      <td className="px-6 py-4 text-right font-medium text-[#24344F]">
                        {Number(item.paidAmount || 0).toFixed(3)}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${item.paymentStatus === "PAID" ? "bg-[#EAF2FF] text-[#1A5EF9]" : "bg-[#FFF4E8] text-[#E08A1E]"}`}
                        >
                          {item.paymentStatus}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-center gap-3">
                          <button
                            className="rounded-[10px] border border-[#D8E4FB] p-2 text-[#6B7C98] transition hover:border-[#B9CEFF] hover:text-[#1A5EF9]"
                            title="Print payment slip"
                            aria-label="Print payment slip"
                            onClick={() => {
                              const payments = invoiceRows.find(
                                (invoice: any) => invoice.id === item.id,
                              )?.paymentResponse;
                              setSlipTransactions(
                                Array.isArray(payments)
                                  ? payments.map((payment: any) => ({
                                      id: payment.paymentId,
                                      name: payment.paymentDate,
                                      date: payment.paymentDate,
                                    }))
                                  : [],
                              );
                              setSlipModalOpen(true);
                            }}
                          >
                            <BadgeDollarSignIcon className="size-4" />
                          </button>
                          <button
                            className="rounded-[10px] border border-[#D8E4FB] p-2 text-[#6B7C98] transition hover:border-[#B9CEFF] hover:text-[#1A5EF9]"
                            title="Download invoice"
                            aria-label="Download invoice"
                            onClick={() => {
                              setDownloadInvoiceId(String(item.id));
                              setDownloadDialogOpen(true);
                            }}
                          >
                            <Printer className="size-4" />
                          </button>
                          <button
                            className="rounded-[10px] border border-[#D8E4FB] p-2 text-[#6B7C98] transition hover:border-[#B9CEFF] hover:text-[#1A5EF9]"
                            title="Create delivery note"
                            aria-label="Create delivery note"
                            onClick={() => {
                              setSelectedDeliveryInvoice(item);
                              setDeliveryNoteModalOpen(true);
                            }}
                          >
                            <Newspaper className="size-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-6 py-16 text-center text-sm text-[#8D98A7]"
                    >
                      No invoices match the current filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <SlipModal
        open={slipModalOpen}
        onCancel={() => setSlipModalOpen(false)}
        onClose={() => setSlipModalOpen(false)}
        transactions={slipTransactions}
        loading={slipLoading}
      />
      <DeliveryNoteModal
        open={deliveryNoteModalOpen}
        invoice={deliveryInvoice}
        isDownloading={
          createDeliveryNote.isPending || downloadDeliveryNotePdf.isPending
        }
        onOpenChange={setDeliveryNoteModalOpen}
        onCreateAndDownload={handleCreateDeliveryNote}
        onDownload={(note) =>
          downloadDeliveryNotePdf.mutate({ deliveryNoteId: note.id })
        }
      />
      <InvoiceDownloadDialog
        open={downloadDialogOpen}
        invoiceId={downloadInvoiceId}
        onClose={() => {
          setDownloadDialogOpen(false);
          setDownloadInvoiceId(null);
        }}
      />
    </>
  );
};

export default Invoice;
